import { Link, NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useState, useRef, useEffect, useCallback } from "react";

const navLinkClass = "px-3 py-2 text-sm font-medium no-underline rounded-lg hover:bg-slate-100 text-slate-700 transition-colors";
const mobileNavLinkClass = "block px-4 py-3 text-sm font-medium no-underline text-slate-700 hover:bg-slate-100 transition-colors";

export default function Header() {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuItemsRef = useRef<(HTMLElement | null)[]>([]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation for dropdown menu
  const handleDropdownKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showDropdown) return;

    const items = menuItemsRef.current.filter(Boolean) as HTMLElement[];
    const currentIndex = items.indexOf(document.activeElement as HTMLElement);

    switch (e.key) {
      case 'Escape':
        setShowDropdown(false);
        // Return focus to trigger button
        (dropdownRef.current?.querySelector('button') as HTMLElement)?.focus();
        e.preventDefault();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (currentIndex < items.length - 1) {
          items[currentIndex + 1]?.focus();
        } else {
          items[0]?.focus();
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (currentIndex > 0) {
          items[currentIndex - 1]?.focus();
        } else {
          items[items.length - 1]?.focus();
        }
        break;
      case 'Tab':
        setShowDropdown(false);
        break;
    }
  }, [showDropdown]);

  // Focus first menu item when dropdown opens
  useEffect(() => {
    if (showDropdown) {
      requestAnimationFrame(() => {
        const items = menuItemsRef.current.filter(Boolean) as HTMLElement[];
        items[0]?.focus();
      });
    }
  }, [showDropdown]);

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur-sm">
      <div className="container-main">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Left: Logo */}
          <Link to="/" className="no-underline shrink-0">
            <div className="leading-tight">
              <div className="text-base font-semibold text-slate-950">UKPropertyChecks</div>
              <div className="text-xs text-slate-600">UK property records and forms</div>
            </div>
          </Link>

          {/* Center: Nav links — collapsed to a single discovery surface
              (per redesign: the property-file is the user-facing primitive;
              individual product pages are no longer top-level destinations). */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Primary">
            <NavLink
              to="/"
              end
              className={({ isActive }) => `${navLinkClass} ${isActive ? "bg-slate-100 text-slate-950" : ""}`}
            >
              Find a property
            </NavLink>
            <NavLink
              to="/pricing"
              className={({ isActive }) => `${navLinkClass} ${isActive ? "bg-slate-100 text-slate-950" : ""}`}
            >
              Pricing
            </NavLink>
            <NavLink
              to="/how-it-works"
              className={({ isActive }) => `${navLinkClass} ${isActive ? "bg-slate-100 text-slate-950" : ""}`}
            >
              How it works
            </NavLink>
          </nav>

          {/* Right: Sign in + mobile menu toggle */}
          <div className="flex items-center gap-3">
            {!isLoading && (
              isAuthenticated ? (
                <div className="relative" ref={dropdownRef} onKeyDown={handleDropdownKeyDown}>
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="btn-secondary btn-sm"
                    aria-expanded={showDropdown}
                    aria-haspopup="true"
                    aria-controls={showDropdown ? "user-menu" : undefined}
                  >
                    {user?.email?.split('@')[0] || 'Account'}
                    <svg className="ml-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showDropdown && (
                    <div id="user-menu" className="absolute right-0 mt-2 w-48 rounded-lg border border-slate-200 bg-white shadow-lg py-1 z-50" role="menu" aria-label="Account menu">
                      <Link
                        ref={(el) => { menuItemsRef.current[0] = el; }}
                        to="/account"
                        className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 no-underline"
                        onClick={() => setShowDropdown(false)}
                        role="menuitem"
                        tabIndex={-1}
                      >
                        Account
                      </Link>
                      <button
                        ref={(el) => { menuItemsRef.current[1] = el; }}
                        onClick={() => {
                          void logout();
                          setShowDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                        role="menuitem"
                        tabIndex={-1}
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="btn-secondary btn-sm no-underline whitespace-nowrap"
                >
                  Sign in
                </Link>
              )
            )}

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden inline-flex items-center justify-center p-2 rounded-lg text-slate-700 hover:bg-slate-100"
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-nav"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              style={{ minHeight: '44px', minWidth: '44px' }}
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile navigation panel */}
      {mobileMenuOpen && (
        <nav id="mobile-nav" className="md:hidden border-t border-slate-200 bg-white" aria-label="Mobile navigation">
          <div className="py-2">
            <NavLink
              to="/"
              end
              className={({ isActive }) => `${mobileNavLinkClass} ${isActive ? "bg-slate-100 text-slate-950" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Find a property
            </NavLink>
            <NavLink
              to="/pricing"
              className={({ isActive }) => `${mobileNavLinkClass} ${isActive ? "bg-slate-100 text-slate-950" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Pricing
            </NavLink>
            <NavLink
              to="/how-it-works"
              className={({ isActive }) => `${mobileNavLinkClass} ${isActive ? "bg-slate-100 text-slate-950" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              How it works
            </NavLink>
          </div>
        </nav>
      )}

      {/* Disclaimer bar - reduced visual weight */}
      <div className="border-t border-slate-100 bg-slate-50/50">
        <div className="container-main py-1.5 text-[11px] text-slate-600">
          Information and document preparation only. Not legal advice.
        </div>
      </div>
    </header>
  );
}
